/**
 * Farm Category Constants for Scope Certificates
 * 
 * These constants define the different categories of process codes used in facility processing
 * and farm capacity calculations throughout the scope certificate system.
 */

// Core farm process codes
export const FARM_CODES = [
  "PR0011", // Farm process code 1
  "PR0036", // Farm process code 2  
  "PR0037", // Farm process code 3
  "PR0038", // Farm process code 4
] as const;

// Supply chain process codes
export const SUPPLY_CHAIN_CODES = [
  "PR0002", // Supply chain process code 1
  "PR0014", // Supply chain process code 2
  "PR0026", // Supply chain process code 3
] as const;

// Exempt combo codes (processes that don't require certain validations)
export const EXEMPT_COMBO_CODES = [
  "PR0000", // Exempt combo code 1
  "PR0030", // Exempt combo code 2
  "PR0048", // Exempt combo code 3
] as const;

// All farm-related categories (farm codes + supply chain codes)
export const ALL_FARM_CATEGORIES = [
  ...FARM_CODES,
  ...SUPPLY_CHAIN_CODES,
] as const;

// Type definitions for better TypeScript support
export type FarmCode = typeof FARM_CODES[number];
export type SupplyChainCode = typeof SUPPLY_CHAIN_CODES[number];
export type ExemptComboCode = typeof EXEMPT_COMBO_CODES[number];
export type FarmCategory = typeof ALL_FARM_CATEGORIES[number];

// Helper functions for common operations
export const FarmCategoryUtils = {
  /**
   * Check if a process code is a farm code
   */
  isFarmCode: (code: string): code is FarmCode => {
    return FARM_CODES.includes(code as FarmCode);
  },

  /**
   * Check if a process code is a supply chain code
   */
  isSupplyChainCode: (code: string): code is SupplyChainCode => {
    return SUPPLY_CHAIN_CODES.includes(code as SupplyChainCode);
  },

  /**
   * Check if a process code is an exempt combo code
   */
  isExemptComboCode: (code: string): code is ExemptComboCode => {
    return EXEMPT_COMBO_CODES.includes(code as ExemptComboCode);
  },

  /**
   * Check if a process code is any farm-related category
   */
  isFarmCategory: (code: string): code is FarmCategory => {
    return ALL_FARM_CATEGORIES.includes(code as FarmCategory);
  },

  /**
   * Get all farm codes as a Set for efficient lookups
   */
  getFarmCodesSet: (): Set<string> => new Set(FARM_CODES),

  /**
   * Get all supply chain codes as a Set for efficient lookups
   */
  getSupplyChainCodesSet: (): Set<string> => new Set(SUPPLY_CHAIN_CODES),

  /**
   * Get all exempt combo codes as a Set for efficient lookups
   */
  getExemptComboCodesSet: (): Set<string> => new Set(EXEMPT_COMBO_CODES),

  /**
   * Get all farm categories as a Set for efficient lookups
   */
  getAllFarmCategoriesSet: (): Set<string> => new Set(ALL_FARM_CATEGORIES),
};

// Farm-based raw material codes.
// These are the ONLY codes that should appear in the Farm Capacity RM code dropdown.
// Source: business spec + confirmed from backend certified-rms.json.
// NOTE: Expand this list if the backend ref data introduces new farm RM codes.
export const FARM_RM_CODES = [
  "RM0003", // Alpaca - Organic
  "RM0004", // Alpaca - RAS
  "RM0005", // Alpaca - Recycled Pre-Consumer
  "RM0006", // Alpaca - Transitional Organic
  "RM0007", // Alpaca/Angora - RAS
  "RM0008", // Bison
  "RM0009", // Camel
  "RM0010", // Cashmere
  "RM0011", // Cashmere - GCS
  "RM0012", // Cashmere - Organic
  "RM0013", // Cashmere - Recycled Pre-Consumer
  "RM0014", // Cashmere - Recycled Post-Consumer
  "RM0015", // Cashmere - RCS
  "RM0016", // Cashmere - Transitional Organic
  "RM0017", // Mohair
  "RM0018", // Mohair - Organic
  "RM0019", // Mohair - RAS
  "RM0020", // Mohair - Recycled Pre-Consumer
  "RM0021", // Mohair - Transitional Organic
  "RM0022", // Qiviut
  "RM0023", // Silk
  "RM0024", // Silk - Organic
  "RM0025", // Silk - Recycled Pre-Consumer
  "RM0026", // Silk - RMS
  "RM0027", // Cotton - Organic
  "RM0028", // Cotton - GOTS
  "RM0029", // Cotton - Transitional Organic
  "RM0030", // Linen (Flax)
  "RM0031", // Down - Organic
  "RM0032", // Down - Responsible (RDS)
  "RM0033", // Down - Recycled Pre-Consumer
  "RM0034", // Down - Recycled Post-Consumer
  "RM0039", // Feather - Responsible (RDS)
  "RM0064", // Wool - Recycled (RMS)
  "RM0083", // Wool - Responsible (RWS)
  "RM0084", // Wool - Organic
  "RM0085", // Wool - Recycled Pre-Consumer
  "RM0086", // Wool - Recycled Post-Consumer
  "RM0087", // Wool - RMS
  "RM0088", // Wool - Transitional Organic
  "RM0328", // Down - RDS
  "RM0329", // Down - RDS
  "RM0330", // Down/Feather - RDS
  "RM0331", // Down - RDS
  "RM0336", // Wool - RWS
  "RM0343", // Wool - RWS
  "RM0350", // Wool/Mohair/Alpaca - RWS + RMS + RAS
  "RM0357", // Wool/Mohair/Alpaca - RWS + RMS + RAS
  "RM0364", // Wool - RWS
  "RM0385", // Mohair - RMS
  "RM0425", // Alpaca - RAS
] as const;

export type FarmRmCode = typeof FARM_RM_CODES[number];
